import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as api from "./api";
import { buildExerciseTemplateUpdatePayload } from "./exerciseTemplatePayload";

// Top-level data hook for the /exercise-templates/:id builder page - loads
// the template plus its two block tables (static elements + interactive
// elements). Block mutations (create/update/delete/move) are issued
// directly against api.js by BlockList.jsx/BlockCard.jsx - this hook only
// owns the reads and the reload trigger they share.
//
// `loading`/the template-header fetch and the blocks fetch are
// DELIBERATELY on two independent effects/triggers, not one shared
// `refresh` counter (that was the original shape, and it caused a real bug:
// reloadBlocks() and the template reload used to be the same function, so
// adding/moving/deleting a single block set `loading=true` and made
// Builder.jsx briefly unmount its ENTIRE body - header, block list, word
// panel - down to a bare "Loading..." placeholder, every single time. That
// collapsed .main-block's scrollHeight and made the browser clamp its
// scrollTop back to 0 - the exact "jumps to the top of the page on every
// edit" complaint. The template header only ever needs refetching once on
// mount (saveTemplateHeader already applies its own response locally, no
// refetch needed after an edit either) - blocksRefresh below is now the
// only thing reloadBlocks touches, and it never toggles `loading`.
export default function useExerciseTemplateBuilder(exerciseId) {
    const navigate = useNavigate();
    const [template, setTemplate] = useState(null);
    const [elements, setElements] = useState([]);
    const [interactiveElements, setInteractiveElements] = useState([]);
    const [containers, setContainers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [blocksRefresh, setBlocksRefresh] = useState(0);

    useEffect(() => {
        setLoading(true);
        api.getExerciseTemplate(exerciseId)
            .then((response) => setTemplate(response.data))
            .catch((err) => {
                if (err.response && err.response.status === 401) navigate("/login");
                // GET /{id} on a missing row is a 500 here, not 404 - still
                // treated the same as "couldn't load" (template stays null).
            })
            .finally(() => setLoading(false));
    }, [exerciseId, navigate]);

    useEffect(() => {
        api.getElements(exerciseId)
            .then((response) => setElements(Array.isArray(response.data) ? response.data : []))
            .catch(() => setElements([]));
        api.getInteractiveElements(exerciseId)
            .then((response) => setInteractiveElements(Array.isArray(response.data) ? response.data : []))
            .catch(() => setInteractiveElements([]));
        api.getContainers(exerciseId)
            .then((response) => setContainers(Array.isArray(response.data) ? response.data : []))
            .catch(() => setContainers([]));
    }, [exerciseId, blocksRefresh]);

    const reloadBlocks = () => setBlocksRefresh((prev) => prev + 1);

    const saveTemplateHeader = (changes) => {
        const payload = buildExerciseTemplateUpdatePayload(template, changes);
        return api.updateExerciseTemplate(exerciseId, payload).then((response) => {
            setTemplate(response.data);
        });
    };

    const deleteTemplate = () => {
        return api.deleteExerciseTemplate(exerciseId).then(() => navigate("/exercise-templates"));
    };

    return { template, elements, interactiveElements, containers, loading, saveTemplateHeader, deleteTemplate, reloadBlocks };
}
